import {
  minutes,
  units as toUnits,
  type AssignmentId,
  type ISODate,
  type ISODateTime,
  type ResourceId,
  type TaskId,
} from '../domain/branded.js';
import type { Assignment } from '../domain/assignment.js';
import type { Calendar } from '../domain/calendar.js';
import type { Diagnostic } from '../domain/errors.js';
import { EntityNotFoundError } from '../domain/errors.js';
import type { Project } from '../domain/project.js';
import type { Resource } from '../domain/resource.js';
import type { Task } from '../domain/task.js';
import type { TimephasedWorkSlice } from '../domain/timephased.js';
import { resolveCalendar, workingMinutesBetween, type ResolvedCalendar } from './calendar-engine.js';
import {
  civilFromDays,
  compareDayMinute,
  daysFromCivil,
  formatDate,
  formatDateTime,
  parseDateTime,
  MINUTES_PER_DAY,
  type DayMinute,
} from './calendar-time.js';
import type { LevelingGranularity, Overallocation } from './leveling.js';
import type { TaskSegment } from './scheduling.js';

/**
 * 資源引擎的底層：日曆解析、逐時段（timephased）攤分、超額配置偵測。
 *
 * 設計約束：
 * - 任何「工作分鐘數」都經由 calendar-engine 的 `workingMinutesBetween()` 取得，
 *   本檔只負責決定「要問哪一段區間」。
 * - 時間點的建構／拆解一律走 calendar-time 的整數工具（不使用 `Date`）。
 * - 工時一律攤在「資源日曆」上（MS Project 語義）；任務日曆只決定任務何時進行。
 */

/** 浮點比較容差（單位為 Units）。 */
const EPSILON = 1e-9;

/** 單一區段最多可跨越的 bucket 數；防止離譜輸入造成長時間迴圈。 */
const MAX_BUCKETS_PER_SEGMENT = 200_000;

// ---------------------------------------------------------------------------
// 日曆集合
// ---------------------------------------------------------------------------

/** 專案內所有日曆的解析快取（每個 id 只解析一次）。 */
export class CalendarSet {
  private readonly definitions = new Map<string, Calendar>();
  private readonly cache = new Map<string, ResolvedCalendar>();
  readonly project: ResolvedCalendar;

  constructor(project: Project) {
    for (const calendar of project.calendars) {
      this.definitions.set(calendar.id as string, calendar);
    }
    this.project = this.byId(project.defaultCalendarId as string);
  }

  byId(id: string): ResolvedCalendar {
    const cached = this.cache.get(id);
    if (cached !== undefined) return cached;
    const definition = this.definitions.get(id);
    if (definition === undefined) throw new EntityNotFoundError('CALENDAR_NOT_FOUND', id);
    const resolved = resolveCalendar(definition, this.definitions);
    this.cache.set(id, resolved);
    return resolved;
  }

  forTask(task: Task): ResolvedCalendar {
    return task.calendarId === null ? this.project : this.byId(task.calendarId as string);
  }

  forResource(resource: Resource): ResolvedCalendar {
    return resource.calendarId === null ? this.project : this.byId(resource.calendarId as string);
  }
}

// ---------------------------------------------------------------------------
// Bucket（時間格）
// ---------------------------------------------------------------------------

/** 取得某時間點所屬 bucket 的索引。 */
export function bucketIndexAt(
  granularity: LevelingGranularity,
  at: DayMinute,
): number {
  switch (granularity) {
    case 'Minute':
      return at.day * MINUTES_PER_DAY + at.minute;
    case 'Hour':
      return at.day * 24 + Math.floor(at.minute / 60);
    case 'Day':
      return at.day;
    case 'Week':
      // 週日對齊：1970-01-01 為週四，故 (day + 4) 後除以 7。
      return Math.floor((at.day + 4) / 7);
    case 'Month': {
      const civil = civilFromDays(at.day);
      return civil.year * 12 + (civil.month - 1);
    }
    default: {
      const exhaustive: never = granularity;
      throw new Error(`Unknown granularity ${String(exhaustive)}`);
    }
  }
}

/** 取得 bucket 的起點（含）。終點即為下一個 bucket 的起點。 */
export function bucketStartOf(
  granularity: LevelingGranularity,
  index: number,
): DayMinute {
  switch (granularity) {
    case 'Minute': {
      const day = Math.floor(index / MINUTES_PER_DAY);
      return { day, minute: index - day * MINUTES_PER_DAY };
    }
    case 'Hour': {
      const day = Math.floor(index / 24);
      return { day, minute: (index - day * 24) * 60 };
    }
    case 'Day':
      return { day: index, minute: 0 };
    case 'Week':
      return { day: index * 7 - 4, minute: 0 };
    case 'Month': {
      const year = Math.floor(index / 12);
      const month = index - year * 12 + 1;
      return { day: daysFromCivil(year, month, 1), minute: 0 };
    }
    default: {
      const exhaustive: never = granularity;
      throw new Error(`Unknown granularity ${String(exhaustive)}`);
    }
  }
}

const atOf = (point: DayMinute): ISODateTime => formatDateTime(point.day, point.minute);

const laterPoint = (a: DayMinute, b: DayMinute): DayMinute =>
  compareDayMinute(a, b) >= 0 ? a : b;
const earlierPoint = (a: DayMinute, b: DayMinute): DayMinute =>
  compareDayMinute(a, b) <= 0 ? a : b;

// ---------------------------------------------------------------------------
// 指派配置（placement）
// ---------------------------------------------------------------------------

/** 一筆指派實際落在時間軸上的位置。 */
export interface Placement {
  readonly assignmentId: AssignmentId;
  readonly taskId: TaskId;
  readonly resourceId: ResourceId;
  /** 投入單位（1 = 100%）。 */
  readonly units: number;
  readonly segments: readonly TaskSegment[];
}

/** 任務時間窗：key 為 task id，value 為該任務的工作區段。 */
export type TaskWindows = ReadonlyMap<string, readonly TaskSegment[]>;

/** 由專案本身的 task.start / task.finish 建立時間窗。 */
export function windowsFromProject(project: Project): TaskWindows {
  const windows = new Map<string, readonly TaskSegment[]>();
  for (const task of project.tasks) {
    windows.set(task.id as string, [{ start: task.start, finish: task.finish }]);
  }
  return windows;
}

/**
 * 建立所有 Work 型資源的指派配置。
 * Material / Cost 型資源不佔用日曆，一律排除（因此永遠不會過度分配）。
 */
export function buildPlacements(
  project: Project,
  windows: TaskWindows,
): readonly Placement[] {
  const tasksById = new Map<string, Task>();
  for (const task of project.tasks) tasksById.set(task.id as string, task);
  const resourcesById = new Map<string, Resource>();
  for (const resource of project.resources) resourcesById.set(resource.id as string, resource);

  const placements: Placement[] = [];
  for (const assignment of project.assignments) {
    const task = tasksById.get(assignment.taskId as string);
    if (task === undefined) continue;
    if (task.isSummary) continue;
    const resource = resourcesById.get(assignment.resourceId as string);
    if (resource === undefined) continue;
    if (resource.type !== 'Work') continue;
    const unitValue = assignment.units as number;
    if (!Number.isFinite(unitValue) || unitValue <= 0) continue;
    const segments = windows.get(task.id as string) ?? [
      { start: task.start, finish: task.finish },
    ];
    placements.push({
      assignmentId: assignment.id,
      taskId: assignment.taskId,
      resourceId: assignment.resourceId,
      units: unitValue,
      segments,
    });
  }
  return placements;
}

// ---------------------------------------------------------------------------
// 逐時段攤分
// ---------------------------------------------------------------------------

/** 單一指派的逐時段工時。 */
export interface AssignmentTimephased {
  readonly assignmentId: AssignmentId;
  readonly taskId: TaskId;
  readonly resourceId: ResourceId;
  readonly granularity: LevelingGranularity;
  readonly work: readonly TimephasedWorkSlice[];
  /** 各切片加總（可能小於 assignment.work：資源日曆沒有的時段無法攤分）。 */
  readonly totalMinutes: number;
}

export interface TimephasedResult {
  readonly entries: readonly AssignmentTimephased[];
  readonly diagnostics: readonly Diagnostic[];
}

interface BucketSlice {
  readonly index: number;
  readonly from: DayMinute;
  readonly to: DayMinute;
  readonly workingMinutes: number;
}

/** 把一個區段切成 bucket，並回傳每個 bucket 內的資源工作分鐘數（>0 者才回傳）。 */
function sliceSegment(
  calendar: ResolvedCalendar,
  granularity: LevelingGranularity,
  segment: TaskSegment,
): readonly BucketSlice[] {
  const from = parseDateTime(segment.start);
  const to = parseDateTime(segment.finish);
  if (compareDayMinute(from, to) >= 0) return [];

  const first = bucketIndexAt(granularity, from);
  const last = bucketIndexAt(granularity, to);
  const slices: BucketSlice[] = [];
  const limit = Math.min(last, first + MAX_BUCKETS_PER_SEGMENT);
  for (let index = first; index <= limit; index += 1) {
    const bucketFrom = bucketStartOf(granularity, index);
    const bucketTo = bucketStartOf(granularity, index + 1);
    const low = laterPoint(from, bucketFrom);
    const high = earlierPoint(to, bucketTo);
    if (compareDayMinute(low, high) >= 0) continue;
    const workingMinutes = workingMinutesBetween(calendar, atOf(low), atOf(high)) as number;
    if (workingMinutes <= 0) continue;
    slices.push({ index, from: low, to: high, workingMinutes });
  }
  return slices;
}

/**
 * 依 `Assignment.units` 與任務時間窗，把工時攤到逐時段（使用資源日曆）。
 * 目前僅實作 Flat contour；其餘 contour 會退回 Flat 並產生 Diagnostic。
 */
export function computeTimephasedWork(
  project: Project,
  granularity: LevelingGranularity,
  windows: TaskWindows = windowsFromProject(project),
  calendars: CalendarSet = new CalendarSet(project),
): TimephasedResult {
  const diagnostics: Diagnostic[] = [];
  const resourcesById = new Map<string, Resource>();
  for (const resource of project.resources) resourcesById.set(resource.id as string, resource);
  const assignmentsById = new Map<string, Assignment>();
  for (const assignment of project.assignments) {
    assignmentsById.set(assignment.id as string, assignment);
  }

  const entries: AssignmentTimephased[] = [];
  for (const placement of buildPlacements(project, windows)) {
    const assignment = assignmentsById.get(placement.assignmentId as string);
    const resource = resourcesById.get(placement.resourceId as string);
    if (assignment === undefined || resource === undefined) continue;
    if (assignment.contour !== undefined && assignment.contour !== 'Flat') {
      diagnostics.push({
        severity: 'Info',
        code: 'WORK_CONTOUR_UNSUPPORTED',
        message: `Assignment ${assignment.id} uses contour ${assignment.contour}; a Flat contour was applied instead.`,
        resourceId: placement.resourceId,
        taskId: placement.taskId,
      });
    }
    const calendar = calendars.forResource(resource);
    const byBucket = new Map<number, { from: DayMinute; to: DayMinute; workMinutes: number }>();
    for (const segment of placement.segments) {
      for (const slice of sliceSegment(calendar, granularity, segment)) {
        const existing = byBucket.get(slice.index);
        const contribution = slice.workingMinutes * placement.units;
        if (existing === undefined) {
          byBucket.set(slice.index, {
            from: slice.from,
            to: slice.to,
            workMinutes: contribution,
          });
        } else {
          existing.from = earlierPoint(existing.from, slice.from);
          existing.to = laterPoint(existing.to, slice.to);
          existing.workMinutes += contribution;
        }
      }
    }
    const work: TimephasedWorkSlice[] = [];
    let totalMinutes = 0;
    for (const index of [...byBucket.keys()].sort((a, b) => a - b)) {
      const bucket = byBucket.get(index);
      if (bucket === undefined) continue;
      totalMinutes += bucket.workMinutes;
      work.push({
        kind: 'Work',
        start: atOf(bucket.from),
        end: atOf(bucket.to),
        valueMinutes: minutes(bucket.workMinutes),
      });
    }
    entries.push({
      assignmentId: placement.assignmentId,
      taskId: placement.taskId,
      resourceId: placement.resourceId,
      granularity,
      work,
      totalMinutes,
    });
  }
  return { entries, diagnostics };
}

// ---------------------------------------------------------------------------
// 可用量
// ---------------------------------------------------------------------------

/** 查詢資源在某日的可用單位（考慮 availability 區間；無命中則用 maxUnits）。 */
export function availableUnitsOn(resource: Resource, date: ISODate): number {
  let value: number | null = null;
  for (const window of resource.availability) {
    if ((window.from as string) > (date as string)) continue;
    if (window.to !== null && (window.to as string) < (date as string)) continue;
    const candidate = window.maxUnits as number;
    value = value === null ? candidate : Math.min(value, candidate);
  }
  return value ?? (resource.maxUnits as number);
}

/**
 * 判定／回報／解決三者共用的基準：**違規最嚴重的那個時點**。
 *
 * 判定用當日可用量（`availability` 在 bucket 中途變動時，取整段最小值會偽陽性、
 * 取整段加權平均會漏判），回報也必須回報同一個時點的 (需求, 容量) 配對，否則
 * 撫平的 resolver 會拿到「需求 < 容量」的矛盾組合而挑不出讓路者。
 */
interface Violation {
  /** 該時點的同時佔用單位數。 */
  readonly demandUnits: number;
  /** **同一時點**的可用單位（當日 availability）。 */
  readonly capacityUnits: number;
}

// ---------------------------------------------------------------------------
// 超額配置偵測
// ---------------------------------------------------------------------------

/** bucket 內的一段實際佔用（已依資源日曆裁切）。 */
interface DemandInterval {
  readonly from: DayMinute;
  readonly to: DayMinute;
  readonly units: number;
  readonly taskId: string;
}

interface BucketDemand {
  readonly index: number;
  readonly intervals: DemandInterval[];
}

interface BucketScan {
  /** 違規最嚴重的時點；null 代表此 bucket 沒有超額。 */
  readonly worst: Violation | null;
  readonly taskIds: readonly string[];
}

/**
 * 掃描一個 bucket 內的實際重疊。
 *
 * 不可用「bucket 內工時總和 ÷ bucket 工作分鐘」的平均值判斷：兩個任務在同一天
 * 各佔半天、但時段真的重疊時，平均後看起來剛好滿載，會漏報（偽陰性）。
 * 這裡逐日、逐「相鄰端點區間」比對同時佔用量與**當日**可用量。
 */
function scanBucket(
  resource: Resource,
  calendar: ResolvedCalendar,
  from: DayMinute,
  to: DayMinute,
  intervals: readonly DemandInterval[],
): BucketScan {
  let worst: Violation | null = null;
  let worstExcess = 0;
  const taskIds = new Set<string>();
  const lastDay = to.minute > 0 ? to.day : to.day - 1;

  for (let day = from.day; day <= lastDay; day += 1) {
    const dayFrom = day === from.day ? from : { day, minute: 0 };
    const dayTo = day === lastDay ? to : { day: day + 1, minute: 0 };
    const capacity = availableUnitsOn(resource, formatDate(day));

    const edges: DayMinute[] = [dayFrom, dayTo];
    const clipped: DemandInterval[] = [];
    for (const interval of intervals) {
      const low = laterPoint(interval.from, dayFrom);
      const high = earlierPoint(interval.to, dayTo);
      if (compareDayMinute(low, high) >= 0) continue;
      clipped.push({ from: low, to: high, units: interval.units, taskId: interval.taskId });
      edges.push(low, high);
    }
    if (clipped.length === 0) continue;
    edges.sort(compareDayMinute);

    for (let i = 0; i < edges.length - 1; i += 1) {
      const low = edges[i];
      const high = edges[i + 1];
      if (low === undefined || high === undefined) continue;
      if (compareDayMinute(low, high) >= 0) continue;
      let concurrent = 0;
      const covering: string[] = [];
      for (const interval of clipped) {
        if (compareDayMinute(interval.from, low) > 0) continue;
        if (compareDayMinute(interval.to, high) < 0) continue;
        concurrent += interval.units;
        covering.push(interval.taskId);
      }
      if (concurrent <= 0) continue;
      // 只有真的落在工作時間上的重疊才算數（午休、非工作日不計）。
      if ((workingMinutesBetween(calendar, atOf(low), atOf(high)) as number) <= 0) continue;
      const excess = concurrent - capacity;
      if (excess <= EPSILON) continue;
      for (const taskId of covering) taskIds.add(taskId);
      if (worst === null || excess > worstExcess) {
        worst = { demandUnits: concurrent, capacityUnits: capacity };
        worstExcess = excess;
      }
    }
  }
  return { worst, taskIds: [...taskIds].sort() };
}

/** 一個 bucket 的超額配置明細（合併前）。 */
interface BucketOverallocation {
  readonly index: number;
  readonly from: ISODateTime;
  readonly to: ISODateTime;
  readonly demandUnits: number;
  readonly capacityUnits: number;
  readonly taskIds: readonly string[];
}

/**
 * 偵測超額配置。回傳結果依「資源 → 時間」排序，相鄰且涉及相同任務集合的
 * bucket 會被合併成一段區間。
 */
export function findOverallocations(
  project: Project,
  placements: readonly Placement[],
  granularity: LevelingGranularity,
  calendars: CalendarSet = new CalendarSet(project),
): readonly Overallocation[] {
  const resourcesById = new Map<string, Resource>();
  for (const resource of project.resources) resourcesById.set(resource.id as string, resource);

  const byResource = new Map<string, Map<number, BucketDemand>>();
  for (const placement of placements) {
    const resource = resourcesById.get(placement.resourceId as string);
    if (resource === undefined) continue;
    const calendar = calendars.forResource(resource);
    let buckets = byResource.get(placement.resourceId as string);
    if (buckets === undefined) {
      buckets = new Map<number, BucketDemand>();
      byResource.set(placement.resourceId as string, buckets);
    }
    for (const segment of placement.segments) {
      for (const slice of sliceSegment(calendar, granularity, segment)) {
        let demand = buckets.get(slice.index);
        if (demand === undefined) {
          demand = { index: slice.index, intervals: [] };
          buckets.set(slice.index, demand);
        }
        demand.intervals.push({
          from: slice.from,
          to: slice.to,
          units: placement.units,
          taskId: placement.taskId as string,
        });
      }
    }
  }

  const result: Overallocation[] = [];
  for (const resourceKey of [...byResource.keys()].sort()) {
    const resource = resourcesById.get(resourceKey);
    const buckets = byResource.get(resourceKey);
    if (resource === undefined || buckets === undefined) continue;
    const calendar = calendars.forResource(resource);

    const flagged: BucketOverallocation[] = [];
    for (const index of [...buckets.keys()].sort((a, b) => a - b)) {
      const demand = buckets.get(index);
      if (demand === undefined) continue;
      const from = bucketStartOf(granularity, index);
      const to = bucketStartOf(granularity, index + 1);
      const capacityMinutes = workingMinutesBetween(calendar, atOf(from), atOf(to)) as number;
      if (capacityMinutes <= 0) continue;
      const scan = scanBucket(resource, calendar, from, to, demand.intervals);
      if (scan.worst === null) continue;
      flagged.push({
        index,
        from: atOf(from),
        to: atOf(to),
        demandUnits: scan.worst.demandUnits,
        capacityUnits: scan.worst.capacityUnits,
        taskIds: scan.taskIds,
      });
    }

    // 合併相鄰且任務集合相同的 bucket。
    let current: BucketOverallocation[] = [];
    const flush = (): void => {
      const first = current[0];
      const last = current[current.length - 1];
      if (first === undefined || last === undefined) return;
      // 合併多個 bucket 時同樣只能取「同一個時點」的配對：取超額最嚴重者，
      // 不可把各 bucket 的最大需求與最小容量拼成一組不存在的組合。
      let demandUnits = first.demandUnits;
      let capacityUnits = first.capacityUnits;
      let excess = first.demandUnits - first.capacityUnits;
      for (const bucket of current) {
        const candidate = bucket.demandUnits - bucket.capacityUnits;
        if (candidate <= excess) continue;
        demandUnits = bucket.demandUnits;
        capacityUnits = bucket.capacityUnits;
        excess = candidate;
      }
      result.push({
        resourceId: resource.id,
        from: first.from,
        to: last.to,
        demandUnits: toUnits(demandUnits),
        capacityUnits: toUnits(capacityUnits),
        taskIds: first.taskIds.map((value) => value as TaskId),
      });
      current = [];
    };
    for (const bucket of flagged) {
      const previous = current[current.length - 1];
      if (
        previous !== undefined &&
        (previous.index + 1 !== bucket.index ||
          previous.taskIds.length !== bucket.taskIds.length ||
          previous.taskIds.some((value, i) => value !== bucket.taskIds[i]))
      ) {
        flush();
      }
      current.push(bucket);
    }
    flush();
  }
  return result;
}

export { EPSILON as OVERALLOCATION_EPSILON };
